import {
  Boxes, PackageCheck, Truck, Factory, Layers, CalendarClock,
  Building2, Warehouse, Plug, BarChart3, ScanLine, Users,
} from "lucide-react";

export const IMAGES = {
  heroWarehouse: "https://images.unsplash.com/photo-1721937127582-ed331de95a04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  aisle: "https://images.unsplash.com/photo-1721937718756-3bfec49f42a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  vizWarehouse: "https://images.unsplash.com/photo-1573205485246-ee99bb898ff6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  operator: "https://images.unsplash.com/photo-1532635026-d12867005472?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  pallets: "https://images.pexels.com/photos/34908275/pexels-photo-34908275.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  containers: "https://images.unsplash.com/photo-1769144256181-698b8f807066?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

export const NAV_LINKS = [
  { label: "Solutions", href: "#solutions" },
  { label: "Features", href: "#features" },
  { label: "Pricing", href: "#pricing" },
  { label: "Resources", href: "/resources" },
];

export const CAPABILITIES = [
  "Inventory", "Inbound", "Outbound", "Planning", "Production",
  "Pallets", "3PL", "Analytics", "Operators", "Integrations",
];

export const FEATURES = [
  { icon: Boxes, title: "Inventory", slug: "inventory", desc: "Real-time stock position across locations and customers — no end-of-day wait.", span: "lg:col-span-2 lg:row-span-2", accent: true },
  { icon: PackageCheck, title: "Inbound", slug: "inbound", desc: "PO → container → receiving → putaway. Know what's coming before it arrives." },
  { icon: Truck, title: "Outbound", slug: "outbound", desc: "Allocate, pick, scan, pack, verify, dispatch — with confidence." },
  { icon: Factory, title: "Production", slug: "production", desc: "Consume raw materials, build finished goods, track every batch." },
  { icon: Layers, title: "Pallets", slug: "pallet-control", desc: "Track CHEP, LOSCAM and more. Reconcile every account." },
  { icon: CalendarClock, title: "Planning", slug: "master-planner", desc: "Model tomorrow before it becomes today's problem.", span: "lg:col-span-2" },
  { icon: Building2, title: "3PL", slug: "3pl", desc: "One platform, multiple customers, complete separation." },
  { icon: Warehouse, title: "Warehouse", desc: "Locations, zones, capacity and slotting under control." },
  { icon: Plug, title: "Integrations", slug: "integrations", desc: "Connect your ERP, carriers and e-commerce stack." },
];

export const SCENARIOS = [
  { id: "01", title: "New customer onboarding", metric: "+2,500 pallets", label: "Capacity impact", value: "+18%", tone: "ok" },
  { id: "02", title: "Four containers arriving", metric: "+100 pallets", label: "Available locations", value: "60", tone: "warn" },
  { id: "03", title: "Demand increase", metric: "+40%", label: "Extra locations needed", value: "+320", tone: "warn" },
];

export const PALLET_FLOW = ["Receive", "Store", "Pick", "Dispatch", "Customer", "Return"];

export const PALLET_LEDGER = [
  { label: "Opening Balance", value: "350", d: "" },
  { label: "Received", value: "+120", d: "up" },
  { label: "Dispatched", value: "-180", d: "down" },
  { label: "Returned", value: "+30", d: "up" },
  { label: "Adjustments", value: "+5", d: "up" },
];

export const PRICING = [
  {
    name: "Starter", tag: "For small warehouse operations",
    price: "Core", blurb: "Core warehouse management to get in control fast.",
    features: ["Inventory & locations", "Inbound & outbound", "Barcode scanning", "1 site · up to 5 operators", "Email support"],
    featured: false,
  },
  {
    name: "Professional", tag: "For growing operations",
    price: "Advanced", blurb: "Advanced management and automation as you scale.",
    features: ["Everything in Starter", "Master Planner", "Pallet Control", "Production module", "Multi-site · unlimited operators", "Priority support"],
    featured: true,
  },
  {
    name: "Enterprise", tag: "For complex, multi-site & 3PL",
    price: "Complete", blurb: "The complete Stockaris platform, built around you.",
    features: ["Everything in Professional", "Full 3PL multi-customer", "Custom integrations & API", "Dedicated success manager", "SLA & onboarding", "SSO & audit logs"],
    featured: false,
  },
];

export const TESTIMONIALS = [
  { quote: "We stopped guessing. Stockaris gave us a live view of stock, pallets and capacity in one place.", name: "Operations Director", company: "National 3PL", metric: "42% fewer stock errors" },
  { quote: "The Master Planner flagged a capacity crunch before Black Friday. That single insight paid for the year.", name: "Head of Supply Chain", company: "FMCG Distributor", metric: "18% capacity reclaimed" },
  { quote: "Pallet reconciliation used to be a spreadsheet nightmare. Now the variance finds itself.", name: "Warehouse Manager", company: "Cold Storage Group", metric: "$140k pallet losses recovered" },
];

export const IMPROVEMENT_OPTIONS = [
  "Inventory accuracy", "Picking", "Receiving", "Warehouse capacity",
  "Pallet control", "3PL", "Production", "Planning", "Other",
];

export const HEALTH_QUESTIONS = [
  { key: "inventory", label: "Inventory Control", q: "How confident are you in your live stock accuracy?" },
  { key: "inbound", label: "Inbound", q: "How smooth is your receiving & putaway process?" },
  { key: "outbound", label: "Outbound", q: "How reliable is your picking & dispatch accuracy?" },
  { key: "pallets", label: "Pallet Control", q: "How well do you track CHEP / LOSCAM balances?" },
  { key: "planning", label: "Planning", q: "How far ahead can you see capacity problems?" },
];

export { BarChart3, ScanLine, Users };
